"use client";
import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../ui/dialog";
import { 
  Search, 
  FileText, 
  ExternalLink,
  Loader2
} from "lucide-react";

interface DocumentItem {
  name: string;
  url: string;
  reference: string;
}

interface LinkDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (documents: DocumentItem[]) => void;
}

export function LinkDocumentDialog({ isOpen, onClose, onLink }: LinkDocumentDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      // Reset selection when dialog opens
      setSelectedDocuments(new Set());
      // Check if SharePoint API is available before attempting to load
      if (!window.sharePointAPI?.loadCloudFiles) {
        console.warn('SharePoint API not available, using fallback data immediately');
        setError('SharePoint API not available. Using sample documents.');
        const mockDocuments: DocumentItem[] = [
          {
            name: "Financial Statements FY2024.xlsx",
            url: "https://example.com/fs2024.xlsx",
            reference: "FS-2024-001"
          },
          {
            name: "Audit Working Papers.docx",
            url: "https://example.com/awp.docx",
            reference: "AWP-2024-001"
          },
          {
            name: "Management Representation Letter.pdf",
            url: "https://example.com/mrl.pdf",
            reference: "MRL-2024-001"
          }
        ];
        setDocuments(mockDocuments);
        setLoading(false);
        return;
      }
      loadDocuments();
    } else {
      // Reset selection when dialog closes
      setSelectedDocuments(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = documents.filter(doc => 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.reference.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredDocuments(filtered);
    } else {
      setFilteredDocuments(documents);
    }
  }, [searchQuery, documents]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    setIsCancelled(false);
    
    // Defer loading to next event loop to prevent UI blocking
    setTimeout(async () => {
      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000) // 30 second timeout
        );
        
        // Load all documents from client folder in db.json
        if (window.sharePointAPI?.loadCloudFiles) {
          const apiPromise = window.sharePointAPI.loadCloudFiles();
          const result = await Promise.race([apiPromise, timeoutPromise]) as any;
          
          // Check if cancelled before processing result
          if (isCancelled) {
            setLoading(false);
            return;
          }
          
          if (result && result.success && result.data && result.data.files) {
            const documents = result.data.files
              .map((f: any) => ({
                name: String(f.name || "").trim(),
                url: f.url || "",
                reference: f.reference || ""
              }))
              .filter((f: any) => f.name && f.name.length > 0);
            setDocuments(documents);
          } else {
            throw new Error('No files found in SharePoint database');
          }
        } else {
          throw new Error('SharePoint API not available');
        }
      } catch (error) {
        console.error('Failed to load documents from SharePoint, using fallback:', error);
        setError('Failed to load documents from SharePoint. Using sample documents.');
        // Use mock data as fallback
        const mockDocuments: DocumentItem[] = [
          {
            name: "Financial Statements FY2024.xlsx",
            url: "https://example.com/fs2024.xlsx",
            reference: "FS-2024-001"
          },
          {
            name: "Audit Working Papers.docx",
            url: "https://example.com/awp.docx", 
            reference: "AWP-2024-002"
          },
          {
            name: "Trial Balance Report.pdf",
            url: "https://example.com/tb.pdf",
            reference: "TB-2024-003"
          }
        ];
        setDocuments(mockDocuments);
      } finally {
        setLoading(false);
      }
    }, 0); // Defer to next event loop
  };

  const handleToggleSelect = (documentName: string) => {
    setSelectedDocuments(prev => {
      const next = new Set(prev);
      if (next.has(documentName)) {
        next.delete(documentName);
      } else {
        next.add(documentName);
      }
      return next;
    });
  };

  const handleLinkSelected = () => {
    const selected = documents.filter(doc => selectedDocuments.has(doc.name));
    if (selected.length > 0) {
      // Clear selection immediately
      setSelectedDocuments(new Set());
      // Close dialog immediately - this must happen synchronously
      onClose();
      
      // Call onLink with significant delay to ensure dialog is fully closed
      // and UI is responsive before processing
      setTimeout(() => {
        onLink(selected);
      }, 100); // Delay to ensure UI is responsive
    }
  };

  // Files now open directly via href links like PPE execution phase

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] border-white/20 bg-black/90 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Link Existing Document</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-sm">
              {error}
            </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
            <Input
              placeholder="Search documents by name or reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-white/20 bg-black/40 text-white placeholder:text-white/50"
            />
          </div>

          {/* Documents List */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-white/70" />
                <span className="ml-2 text-white/70">Loading documents...</span>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-8 text-white/60">
                {searchQuery ? 'No documents found matching your search.' : 'No documents available.'}
              </div>
            ) : (
              filteredDocuments.map((doc, index) => {
                const isSelected = selectedDocuments.has(doc.name);
                return (
                  <div 
                    key={index}
                    className={`flex items-center justify-between rounded-md border p-3 transition-colors cursor-pointer ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-500/20' 
                        : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                    }`}
                    onClick={() => handleToggleSelect(doc.name)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(doc.name)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-white/20 bg-black/40 text-blue-600 focus:ring-blue-500"
                      />
                      <FileText className="h-5 w-5 text-white/70 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        {doc.url ? (
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-sm text-blue-300 hover:underline truncate block font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {doc.name}
                          </a>
                        ) : (
                          <div className="text-sm text-white truncate font-medium">
                            {doc.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-white/70">
              {selectedDocuments.size > 0 && `${selectedDocuments.size} file(s) selected`}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCancelled(true);
                  setLoading(false);
                  setSelectedDocuments(new Set());
                  onClose();
                }}
                className="border-white/20 bg-transparent text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLinkSelected}
                disabled={selectedDocuments.size === 0 || loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Link Selected ({selectedDocuments.size})
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
